<!--
// Copyright © 2020 Anticrm Platform Contributors.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte'
  import { LabelAndProps } from '../types'
  import { tooltip } from '../tooltips'

  export let id: string | undefined = undefined
  export let on: boolean = false
  export let disabled: boolean = false
  export let showTooltip: LabelAndProps | undefined = undefined

  const dispatch = createEventDispatcher()
</script>

<label {id} class="toggle" use:tooltip={showTooltip} class:disabled {...$$restProps}>
  <input
    class="chBox"
    type="checkbox"
    {disabled}
    bind:checked={on}
    on:change={(e) => {
      dispatch('change', on)
    }}
  />
  <span class="toggle-switch" />
</label>

<style lang="scss">
  // Toggle switch recipe: 44x24 pill, CTA gradient when on.
  .toggle {
    display: inline-block;
    width: 2.75rem;
    min-width: 2.75rem;
    height: 1.5rem;
    vertical-align: middle;
    font-size: inherit;
    -webkit-user-select: none;
    user-select: none;
    cursor: pointer;

    &.disabled {
      cursor: default;
    }

    .chBox {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      border: 0;
      padding: 0;
      clip: rect(0 0 0 0);
      overflow: hidden;

      &:checked + .toggle-switch {
        background: var(--cta-gradient);
        &:hover {
          background: var(--cta-gradient);
          filter: brightness(1.1);
        }
        &:before {
          transform: translateX(1.25rem);
          background: var(--theme-toggle-on-sw-color);
        }
      }
      &:disabled + .toggle-switch {
        opacity: 0.4;
        filter: none;
      }
      &:focus-visible + .toggle-switch {
        outline: 2px solid var(--focus-ring);
        outline-offset: 2px;
      }
    }
    .toggle-switch {
      position: relative;
      display: inline-block;
      width: 2.75rem;
      height: 1.5rem;
      border-radius: var(--full-BorderRadius);
      background: var(--theme-toggle-bg-color);
      transition:
        background var(--duration-fast) var(--timing-standard),
        filter var(--duration-fast) var(--timing-standard);
      &:before {
        content: '';
        position: absolute;
        top: 0.125rem;
        left: 0.125rem;
        display: inline-block;
        width: 1.25rem;
        height: 1.25rem;
        border-radius: 50%;
        background: var(--theme-toggle-sw-color);
        transition: transform var(--duration-fast) var(--timing-standard);
        @media (prefers-reduced-motion: reduce) {
          transition: none;
        }
      }
      &:hover {
        background: var(--theme-toggle-bg-hover);
      }
    }
  }
</style>
